/*
 * ReportView — one fleet report as a chart + grid (NEXUS-1384). Used inline in the assistant
 * drawer, in pinned Dashboard cards and in the full-screen report view, so a pinned card looks
 * exactly like what was pinned.
 *
 * The grid (DataGrid) has sort + type-ahead filters on every column; the chart follows the same
 * filtered + sorted rows, so filtering the grid re-draws the chart. `handleRef` exposes an HTML
 * export of the current view (chart SVG + filtered grid).
 */

import { useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ReportResult } from '../lib/reports';
import DataGrid, { applyView, EMPTY_VIEW, formatCell, type GridView, type Row } from './DataGrid';
import { buildReportHtml, chartSvgFrom, downloadHtml, slugify } from '../lib/exportHtml';

const SERIES_COLORS = ['#019ddc', '#c14545', '#2eb872', '#e8a93a'];

function shortLabel(v: unknown): string {
  const s = String(v ?? '');
  // "DEV LAB 0C6F (EPV_SPV_IOT_286DCE300C6F)" → "DEV LAB 0C6F" on the axis; the grid keeps it whole.
  const cut = s.replace(/\s*\(EPV_SPV_IOT_[0-9A-F]+\)$/i, '');
  return cut.length > 18 ? `${cut.slice(0, 17)}…` : cut;
}

export interface ReportExportMeta {
  title?: string | null;
  pinnedBy?: string | null;
  pinnedAt?: string | null;
}

export interface ReportViewHandle {
  exportHtml: (meta?: ReportExportMeta) => void;
  /** Rows currently shown (filtered) / total. */
  counts: () => { shown: number; total: number };
}

export interface ReportViewProps {
  result: ReportResult;
  /** 'chart' | 'table' | 'both' — default both (chart above the grid). */
  mode?: 'chart' | 'table' | 'both';
  compact?: boolean;
  maxRows?: number;
  chartHeight?: number;
  gridMaxHeight?: number;
  handleRef?: MutableRefObject<ReportViewHandle | null>;
  /** Starting sort / filters (e.g. carried from a card into full screen). */
  initialView?: GridView;
  onViewChange?: (v: GridView) => void;
}

export default function ReportView({
  result,
  mode = 'both',
  compact = false,
  maxRows,
  chartHeight,
  gridMaxHeight,
  handleRef,
  initialView,
  onViewChange,
}: ReportViewProps) {
  const [view, setViewState] = useState<GridView>(initialView ?? EMPTY_VIEW);
  const chartBox = useRef<HTMLDivElement>(null);
  const setView = (v: GridView) => {
    setViewState(v);
    onViewChange?.(v);
  };
  const rows = result.rows as Row[];
  const visible = useMemo(() => applyView(result.columns, rows, view), [result.columns, rows, view]);
  const chart = result.chart;
  // Line charts are time series — keep them chronological whatever the grid sort is.
  const chartRows = useMemo(() => {
    if (!chart) return [];
    if (chart.type === 'line') {
      const keep = new Set(visible);
      return rows.filter((r) => keep.has(r));
    }
    return visible.slice(0, compact ? 15 : 40);
  }, [chart, visible, rows, compact]);
  const height = chartHeight ?? (compact ? 180 : 240);

  useEffect(() => {
    if (!handleRef) return;
    handleRef.current = {
      counts: () => ({ shown: visible.length, total: rows.length }),
      exportHtml: (meta) => {
        const filters = result.columns
          .map((c) => ({ label: c.label, values: view.filters[c.key] ?? [] }))
          .filter((f) => f.values.length > 0);
        const sortCol = view.sortKey ? result.columns.find((c) => c.key === view.sortKey) : undefined;
        const html = buildReportHtml({
          result,
          title: meta?.title,
          rows: visible,
          totalRows: rows.length,
          filters,
          sortLabel: sortCol ? `${sortCol.label} (${view.sortDir === 'asc' ? 'ascending' : 'descending'})` : null,
          chartSvg: mode !== 'table' ? chartSvgFrom(chartBox.current) : null,
          pinnedBy: meta?.pinnedBy,
          pinnedAt: meta?.pinnedAt,
          origin: typeof window !== 'undefined' ? window.location.origin : undefined,
        });
        const day = new Date().toISOString().slice(0, 10);
        downloadHtml(`${slugify(meta?.title || result.title)}-${day}.html`, html);
      },
    };
  });

  return (
    <div data-testid={`report-${result.report}`}>
      {rows.length === 0 ? (
        <div className="text-muted small py-3 text-center">No data for this period.</div>
      ) : (
        <>
          {chart && mode !== 'table' && (
            <div ref={chartBox} style={{ width: '100%', height }} data-testid="report-chart">
              {chartRows.length === 0 ? (
                <div className="h-100 d-flex align-items-center justify-content-center text-muted small">
                  No rows match the filters.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  {chart.type === 'line' ? (
                    <LineChart data={chartRows} margin={{ top: 8, right: 12, bottom: 4, left: -16 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(16,28,66,0.08)" />
                      <XAxis
                        dataKey={chart.xKey}
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v) => formatCell({ type: 'date' }, v)}
                        minTickGap={16}
                      />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip labelFormatter={(v) => formatCell({ type: 'date' }, v)} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {chart.yKeys.map((y, i) => (
                        <Line
                          key={y.key}
                          type="monotone"
                          dataKey={y.key}
                          name={y.label}
                          stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                          strokeWidth={2}
                          dot={false}
                          isAnimationActive={false}
                        />
                      ))}
                    </LineChart>
                  ) : (
                    <BarChart data={chartRows} margin={{ top: 8, right: 12, bottom: 4, left: -16 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(16,28,66,0.08)" vertical={false} />
                      <XAxis
                        dataKey={chart.xKey}
                        tick={{ fontSize: 11 }}
                        tickFormatter={shortLabel}
                        interval={chartRows.length > (compact ? 6 : 12) ? 'preserveStartEnd' : 0}
                      />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip cursor={{ fill: 'rgba(1,157,220,0.06)' }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {chart.yKeys.map((y, i) => (
                        <Bar
                          key={y.key}
                          dataKey={y.key}
                          name={y.label}
                          fill={SERIES_COLORS[i % SERIES_COLORS.length]}
                          radius={[3, 3, 0, 0]}
                          maxBarSize={56}
                          isAnimationActive={false}
                        />
                      ))}
                    </BarChart>
                  )}
                </ResponsiveContainer>
              )}
            </div>
          )}
          {mode !== 'chart' && (
            <div className="mt-2">
              <DataGrid
                columns={result.columns}
                rows={rows}
                visible={visible}
                view={view}
                onView={setView}
                maxHeight={gridMaxHeight ?? (compact ? 240 : 320)}
                maxRows={maxRows}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
